import { CSSProperties, useState } from "react";

import { Icon, InputGroup, Text } from "@blueprintjs/core";

import { Flex } from "@/components/layout/flexbox";
import { BRAND_PURPLE, MAINTEXT_COLOR } from "@/css/constants";
import { cn } from "@/lib/utils";

interface InlineEditableTextProps {
  onSave?: (value: string) => void;
  value: string;
  className?: string;
  style?: CSSProperties;
  isEditable?: boolean;
}

export function InlineEditableText({
  value,
  onSave,
  className,
  style,
  isEditable,
}: InlineEditableTextProps) {
  const [editedValue, setEditedValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  if (isEditing) {
    return (
      <InputGroup
        autoFocus
        onAbortCapture={() => {
          setIsEditing(false);
          setIsHovering(false);
        }}
        onBlur={() => {
          if (editedValue === "") {
            // Reset empty value to original value
            onSave?.(value);
            setEditedValue(value);
          } else {
            onSave?.(editedValue);
          }
          setIsEditing(false);
          setIsHovering(false);
        }}
        onChange={(event) => {
          setEditedValue(event.target.value);
        }}
        value={editedValue}
      />
    );
  }

  return (
    <Flex
      justifyContent="space-between"
      style={{ gap: "4px" }}
      alignItems="center"
    >
      <Text
        style={{
          ...style,
          flex: 1,
          cursor: isHovering ? "pointer" : "default",
          border: isHovering ? `1px solid ${BRAND_PURPLE}` : "none",
          borderRadius: isHovering ? "8px" : "0",
          padding: isEditable ? "4px" : "0",
          color: MAINTEXT_COLOR,
        }}
        className={cn(className, "line-clamp-2")}
        onClick={() => {
          if (isEditable) {
            setIsEditing(true);
          }
        }}
        onMouseLeave={() => {
          if (isEditable) {
            setIsHovering(false);
          }
        }}
        onMouseEnter={() => {
          if (isEditable) {
            setIsHovering(true);
          }
        }}
      >
        {value}
      </Text>
      <Icon
        style={{
          padding: "4px",
          display: isHovering ? "block" : "none",
        }}
        icon="edit"
      />
    </Flex>
  );
}
